import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { useAuth } from '../contexts/AuthContext';
import api, { companiesAPI, usersAPI } from '../lib/api';
import TemplateSelector from '../components/TemplateSelector';
import DataImport from "../components/DataImport";
import { toast } from 'sonner';
import { Settings as SettingsIcon, User, Mail, Lock, Bell, Palette, Globe, Building2 } from 'lucide-react';

const Settings = () => {
  const { user, currentCompany, switchCompany } = useAuth();
  const [seedStatus, setSeedStatus] = useState(null);
  const [seedBusy, setSeedBusy] = useState(false);
  const [seedMessage, setSeedMessage] = useState("");
  const loadSeedStatus = async () => {
    try {
      const res = await api.get("/setup/seed-catalogue");
      setSeedStatus(res.data);
    } catch (e) {
      setSeedStatus(null);
    }
  };
  useEffect(() => {
    if (currentCompany) {
      loadSeedStatus();
    }
  }, [currentCompany]);
  const handleSeedCatalogue = async () => {
    setSeedBusy(true);
    setSeedMessage("");
    try {
      const res = await api.post("/setup/seed-catalogue");
      setSeedMessage("Seeded " + res.data.inserted + " Elite catalogue products.");
      toast.success("Elite catalogue seeded.");
      loadSeedStatus();
    } catch (e) {
      const detail = e.response && e.response.data && e.response.data.detail ? e.response.data.detail : "Failed to seed the Elite catalogue.";
      setSeedMessage(detail);
      toast.error(detail);
    } finally {
      setSeedBusy(false);
    }
  };
  const [profileForm, setProfileForm] = useState({ name: '', email: '' });
  const [profileSaving, setProfileSaving] = useState(false);

  useEffect(() => {
    if (user) {
      setProfileForm({ name: user.name || '', email: user.email || '' });
    }
  }, [user]);

  

  // Company settings (Settings foundation, Round R2): the active company record
  // is loaded from the Companies API and saved back through it. Later rounds
  // extend this area with branding fields (R8), integration keys (R7) and
  // agent tokens (V4); those sections are intentionally not built here.
  const [companyId, setCompanyId] = useState(null);
  const [companyForm, setCompanyForm] = useState({
    name: '',
    address: '',
    email: '',
    phone: '',
    tax_id: '',
    bank_name: '',
    bank_account: '',
    currency: '',
      code: '',
      country: '',
      status: 'active',
      default_document_template: 'classic',
      default_template_invoice: 'classic',
      default_template_quotation: 'classic',
      default_template_sales_order: 'classic',
      default_template_delivery_challan: 'classic',
      default_template_payment_receipt: 'classic',
      default_template_credit_note: 'classic',
      default_template_estimate: 'classic',
      logo_url: '',
        auto_journal_enabled: false,
  });
  const [companyLoading, setCompanyLoading] = useState(true);
  const [companySaving, setCompanySaving] = useState(false);
  const [companyError, setCompanyError] = useState('');
  // Maya integration keys (Round R7): stored per company, never shown back.
  const [mayaToken, setMayaToken] = useState("");
  const [mayaTokenSaved, setMayaTokenSaved] = useState(false);
  const [mayaTokenLast4, setMayaTokenLast4] = useState("");
  const [mayaSaving, setMayaSaving] = useState(false);
  const [mayaCheckMessage, setMayaCheckMessage] = useState("");
  const mayaApiBase = (api.defaults && api.defaults.baseURL) || "";
  const mayaApiOrigin = mayaApiBase.endsWith("/api") ? mayaApiBase.slice(0, mayaApiBase.length - 4) : mayaApiBase;
  const mayaReceiverUrl = (mayaApiOrigin || window.location.origin) + "/api/integrations/maya/events";
  const loadMayaSettings = async () => {
    try {
      const res = await api.get("/integrations/settings");
      const data = res.data || {};
      setMayaTokenSaved(!!data.maya_api_token_set);
      setMayaTokenLast4(data.maya_api_token_last4 || "");
    } catch (e) {
      setMayaTokenSaved(false);
      setMayaTokenLast4("");
    }
  };
  useEffect(() => {
    if (currentCompany) {
      loadMayaSettings();
    }
  }, [currentCompany]);
  const saveMayaToken = async () => {
    if (!mayaToken.trim()) {
      toast.error("Enter a Maya API token to save.");
      return;
    }
    setMayaSaving(true);
    try {
      const res = await api.put("/integrations/settings", { maya_api_token: mayaToken.trim() });
      const data = res.data || {};
      setMayaTokenSaved(!!data.maya_api_token_set);
      setMayaTokenLast4(data.maya_api_token_last4 || "");
      setMayaToken("");
      toast.success("Maya API token saved.");
    } catch (e) {
      toast.error("Failed to save Maya API token.");
    } finally {
      setMayaSaving(false);
    }
  };
  const removeMayaToken = async () => {
    setMayaSaving(true);
    try {
      await api.put("/integrations/settings", { maya_api_token: null });
      setMayaTokenSaved(false);
      setMayaTokenLast4("");
      setMayaToken("");
      toast.success("Maya API token removed.");
    } catch (e) {
      toast.error("Failed to remove Maya API token.");
    } finally {
      setMayaSaving(false);
    }
  };
  const checkMayaSetup = async () => {
    try {
      const res = await api.get("/integrations/settings");
      const data = res.data || {};
      if (data.maya_api_token_set) {
        setMayaCheckMessage("Ready: a Maya API token is saved for this company. Maya can POST events to " + mayaReceiverUrl + ".");
      } else {
        setMayaCheckMessage("Not ready: no Maya API token is saved for this company yet. Receiver URL: " + mayaReceiverUrl + ".");
      }
    } catch (e) {
      setMayaCheckMessage("Could not check the Maya setup. Please try again.");
    }
  };

  const applyCompanyToForm = (company) => {
    setCompanyId(company.id);
    setCompanyForm({
      name: company.name || '',
      address: company.address || '',
      email: company.email || '',
      phone: company.phone || '',
      tax_id: company.tax_id || '',
      bank_name: company.bank_name || '',
      bank_account: company.bank_account || '',
      currency: company.currency || '',
      code: company.code || '',
      country: company.country || '',
      status: company.status || 'active',
      default_document_template: company.default_document_template || 'classic',
      default_template_invoice: company.default_template_invoice || 'classic',
      default_template_quotation: company.default_template_quotation || 'classic',
      default_template_sales_order: company.default_template_sales_order || 'classic',
      default_template_delivery_challan: company.default_template_delivery_challan || 'classic',
      default_template_payment_receipt: company.default_template_payment_receipt || 'classic',
      default_template_credit_note: company.default_template_credit_note || 'classic',
      default_template_estimate: company.default_template_estimate || 'classic',
      logo_url: company.logo_url || '',
        auto_journal_enabled: company.auto_journal_enabled || false,
    });
  };
  useEffect(() => {
    const loadCompany = async () => {
      try {
        let company = null;
        if (currentCompany?.id) {
          try {
            const res = await companiesAPI.getById(currentCompany.id);
            company = res.data?.company || res.data || null;
          } catch (e) {
            company = null;
          }
        }
        if (company === null) {
          const res = await companiesAPI.getAll();
          const list = res.data?.companies || res.data || [];
          const arr = Array.isArray(list) ? list : [];
          company = arr.find((c) => c.id === currentCompany?.id) || arr[0] || null;
        }
        if (company) {
          applyCompanyToForm(company);
        } else if (currentCompany) {
          applyCompanyToForm(currentCompany);
        }
      } catch (error) {
        setCompanyError('Failed to load company information.');
      } finally {
        setCompanyLoading(false);
      }
    };
    loadCompany();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentCompany?.id]);
  const handleProfileSave = async () => {
    if (!user?.id) {
      toast.error('No user is signed in.');
      return;
    }
    setProfileSaving(true);
    try {
      await usersAPI.update(user.id, { name: profileForm.name, email: profileForm.email });
      toast.success('Profile saved successfully');
    } catch (error) {
      toast.error('Failed to save profile.');
    } finally {
      setProfileSaving(false);
    }
  };

  const handleLogoUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 1024 * 1024) {
      toast.error('Logo image must be smaller than 1 MB.');
      e.target.value = '';
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      setCompanyForm((prev) => ({ ...prev, logo_url: reader.result }));
    };
    reader.readAsDataURL(file);
    e.target.value = '';
  };

  const handleLogoRemove = () => {
    setCompanyForm((prev) => ({ ...prev, logo_url: '' }));
  };

  const handleCompanySave = async () => {
    if (companyId == null) {
      toast.error('No company selected to save.');
      return;
    }
    setCompanySaving(true);
    try {
      const payload = { ...companyForm, code: companyForm.code || currentCompany?.code || '', country: companyForm.country || currentCompany?.country || '' };
      await companiesAPI.update(companyId, payload);
      if (switchCompany && currentCompany) {
        await switchCompany({ ...currentCompany, ...companyForm });
      }
      toast.success('Company settings saved successfully');
    } catch (error) {
      toast.error((() => { const detail = error?.response?.data?.detail; if (typeof detail === 'string') return detail; if (Array.isArray(detail)) return detail.map((item) => item.msg).filter(Boolean).join('; '); if (detail) { if (typeof detail.msg === 'string') return detail.msg; } return null; })() || 'Failed to save company settings.');
    } finally {
      setCompanySaving(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-heading text-2xl font-bold text-slate-900">Settings</h1>
        <p className="text-slate-500 mt-1">Manage your account and application settings</p>
      </div>

      <Tabs defaultValue="profile" className="space-y-6">
        <TabsList className="bg-slate-100">
          <TabsTrigger value="profile" className="data-[state=active]:bg-white">
            <User className="h-4 w-4 mr-2" />
            Profile
          </TabsTrigger>
          <TabsTrigger value="notifications" className="data-[state=active]:bg-white">
            <Bell className="h-4 w-4 mr-2" />
            Notifications
          </TabsTrigger>
          <TabsTrigger value="appearance" className="data-[state=active]:bg-white">
            <Palette className="h-4 w-4 mr-2" />
            Appearance
          </TabsTrigger>
          <TabsTrigger value="company" className="data-[state=active]:bg-white">
            <Building2 className="h-4 w-4 mr-2" />
            Company
          </TabsTrigger>
        <TabsTrigger value="import" className="data-[state=active]:bg-white">Data Import</TabsTrigger>
        </TabsList>

        {/* Profile Tab */}
        <TabsContent value="profile">
          <Card className="bg-white border-slate-200">
            <CardHeader>
              <CardTitle className="font-heading text-lg">Profile Settings</CardTitle>
              <CardDescription>Manage your personal information</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="name">Full Name</Label>
                  <Input
                    id="name"
                    value={profileForm.name} onChange={(e) => setProfileForm({ ...profileForm, name: e.target.value })}
                    placeholder="Your name"
                    className="h-10"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email">Email Address</Label>
                  <Input
                    id="email"
                    type="email"
                    value={profileForm.email} onChange={(e) => setProfileForm({ ...profileForm, email: e.target.value })}
                    placeholder="you@example.com"
                    className="h-10"
                  />
                </div>
              </div>

              <div className="pt-4 border-t border-slate-200">
                <h3 className="font-medium text-slate-900 mb-4 flex items-center gap-2">
                  <Lock className="h-4 w-4" />
                  Change Password
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="current-password">Current Password</Label>
                    <Input
                      id="current-password"
                      type="password"
                      placeholder="••••••••"
                      className="h-10"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="new-password">New Password</Label>
                    <Input
                      id="new-password"
                      type="password"
                      placeholder="••••••••"
                      className="h-10"
                    />
                  </div>
                </div>
              </div>

              <div className="flex justify-end pt-4">
                <Button 
                  onClick={handleProfileSave}
            disabled={profileSaving}
            className="bg-teal-700 hover:bg-teal-800"
            data-testid="save-profile-btn"
          >
            {profileSaving ? "Saving..." : "Save Changes"}
                </Button>
              </div>
              <p className="text-sm text-slate-500">Password changes are not available yet: the app has no password-change service configured.</p>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Notifications Tab */}
        <TabsContent value="notifications">
          <Card className="bg-white border-slate-200">
            <CardHeader>
              <CardTitle className="font-heading text-lg">Notification Preferences</CardTitle>
              <CardDescription>Control how you receive notifications</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium text-slate-900">Email Notifications</p>
                    <p className="text-sm text-slate-500">Receive email updates for important events</p>
                  </div>
                  <Switch defaultChecked />
                </div>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium text-slate-900">New Invoice Alerts</p>
                    <p className="text-sm text-slate-500">Get notified when new invoices are created</p>
                  </div>
                  <Switch defaultChecked />
                </div>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium text-slate-900">Payment Reminders</p>
                    <p className="text-sm text-slate-500">Receive reminders for upcoming payments</p>
                  </div>
                  <Switch defaultChecked />
                </div>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium text-slate-900">Low Stock Alerts</p>
                    <p className="text-sm text-slate-500">Get notified when inventory is running low</p>
                  </div>
                  <Switch defaultChecked />
                </div>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium text-slate-900">Task Assignments</p>
                    <p className="text-sm text-slate-500">Notifications for new task assignments</p>
                  </div>
                  <Switch defaultChecked />
                </div>
              </div>

              <div className="flex justify-end pt-4">
                <Button 
                  disabled
                  className="bg-teal-700 hover:bg-teal-800"
                >
                  Save Preferences
                </Button>
              </div>
              <p className="text-sm text-slate-500">Notification preferences are not saved yet: the app has no notification service or preferences storage configured.</p>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Appearance Tab */}
        <TabsContent value="appearance">
          <Card className="bg-white border-slate-200">
            <CardHeader>
              <CardTitle className="font-heading text-lg">Appearance Settings</CardTitle>
              <CardDescription>Customize the look and feel of your dashboard</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-4">
                <div>
                  <Label className="text-slate-700 mb-3 block">Theme</Label>
                  <div className="flex gap-4">
                    <button className="flex-1 p-4 border-2 border-teal-600 rounded-lg bg-white">
                      <div className="h-8 bg-white border border-slate-200 rounded mb-2"></div>
                      <p className="text-sm font-medium text-slate-900">Light</p>
                    </button>
                    <button className="flex-1 p-4 border border-slate-200 rounded-lg bg-slate-50 opacity-50 cursor-not-allowed">
                      <div className="h-8 bg-slate-800 rounded mb-2"></div>
                      <p className="text-sm font-medium text-slate-900">Dark (Coming Soon)</p>
                    </button>
                  </div>
                </div>

                <div className="pt-4">
                  <Label className="text-slate-700 mb-3 block">Primary Color</Label>
                  <div className="flex gap-3">
                    <button className="w-10 h-10 rounded-lg bg-teal-700 ring-2 ring-teal-700 ring-offset-2"></button>
                    <button className="w-10 h-10 rounded-lg bg-blue-600"></button>
                    <button className="w-10 h-10 rounded-lg bg-purple-600"></button>
                    <button className="w-10 h-10 rounded-lg bg-rose-600"></button>
                    <button className="w-10 h-10 rounded-lg bg-amber-600"></button>
                  </div>
                  <p className="text-xs text-slate-500 mt-2">Color customization coming soon</p>
                </div>

                <div className="pt-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium text-slate-900">Compact Mode</p>
                      <p className="text-sm text-slate-500">Reduce spacing for more content</p>
                <p className="text-xs text-slate-500 mt-1">Compact mode is not available yet.</p>
                    </div>
                    <Switch disabled />
                  </div>
                </div>
              </div>

              <div className="flex justify-end pt-4">
                <Button 
                  disabled
                  className="bg-teal-700 hover:bg-teal-800"
                >
                  Save Appearance
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      {/* Company Tab */}
      <TabsContent value="company">
        <Card className="bg-white border-[#E5E2D9]">
          <CardHeader>
            <CardTitle className="font-heading text-lg">Company Information</CardTitle>
            <CardDescription>Your company details as they appear on invoices, quotations and other documents.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {companyLoading ? (
              <p className="text-sm text-[#5C6660]">Loading company information...</p>
            ) : companyError ? (
              <p className="text-sm text-[#C93A3A]">{companyError}</p>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="company-name">Company Name *</Label>
                  <Input
                    id="company-name"
                    data-testid="company-name-input"
                    value={companyForm.name}
                    onChange={(e) => setCompanyForm({ ...companyForm, name: e.target.value })}
                    className="h-10"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="company-email">Email</Label>
                  <Input
                    id="company-email"
                    value={companyForm.email}
                    onChange={(e) => setCompanyForm({ ...companyForm, email: e.target.value })}
                    className="h-10"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="company-phone">Phone</Label>
                  <Input
                    id="company-phone"
                    value={companyForm.phone}
                    onChange={(e) => setCompanyForm({ ...companyForm, phone: e.target.value })}
                    className="h-10"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="company-tax-id">Tax ID</Label>
                  <Input
                    id="company-tax-id"
                    value={companyForm.tax_id}
                    onChange={(e) => setCompanyForm({ ...companyForm, tax_id: e.target.value })}
                    className="h-10"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="company-currency">Currency</Label>
                  <Input
                    id="company-currency"
                    value={companyForm.currency}
                    onChange={(e) => setCompanyForm({ ...companyForm, currency: e.target.value })}
                    className="h-10"
                  />
                </div>
                <div className="space-y-2 md:col-span-2">
                  <Label htmlFor="company-address">Address</Label>
                  <Input
                    id="company-address"
                    value={companyForm.address}
                    onChange={(e) => setCompanyForm({ ...companyForm, address: e.target.value })}
                    className="h-10"
                  />
                </div>
      <div className="space-y-2">
        <Label htmlFor="company-bank-name">Bank Name</Label>
        <Input id="company-bank-name" value={companyForm.bank_name} onChange={(e) => setCompanyForm({ ...companyForm, bank_name: e.target.value })} className="h-10" />
      </div>
      <div className="space-y-2">
        <Label htmlFor="company-bank-account">Bank Account</Label>
        <Input id="company-bank-account" value={companyForm.bank_account} onChange={(e) => setCompanyForm({ ...companyForm, bank_account: e.target.value })} className="h-10" />
      </div>
      <div className="space-y-4 md:col-span-2 pt-4 border-t border-slate-200">
        <div className="space-y-2">
          <h3 className="font-heading text-base">Document Templates</h3>
          <p className="text-sm text-slate-500">Each document type renders with its own saved default template. A type with no saved default renders as Classic. These defaults apply to every document; there is no per-document template choice.</p>
        </div>
        <div className="space-y-2">
          <Label>Default Document Template</Label>
          <TemplateSelector selectedTemplate={companyForm.default_document_template || 'classic'} onSelect={(id) => setCompanyForm({ ...companyForm, default_document_template: id })} />
        </div>
        <div className="space-y-2">
          <Label>Invoice</Label>
          <TemplateSelector selectedTemplate={companyForm.default_template_invoice || 'classic'} onSelect={(id) => setCompanyForm({ ...companyForm, default_template_invoice: id })} />
        </div>
        <div className="space-y-2">
          <Label>Quotation</Label>
          <TemplateSelector selectedTemplate={companyForm.default_template_quotation || 'classic'} onSelect={(id) => setCompanyForm({ ...companyForm, default_template_quotation: id })} />
        </div>
        <div className="space-y-2">
          <Label>Sales Order</Label>
          <TemplateSelector selectedTemplate={companyForm.default_template_sales_order || 'classic'} onSelect={(id) => setCompanyForm({ ...companyForm, default_template_sales_order: id })} />
        </div>
        <div className="space-y-2">
          <Label>Delivery Challan</Label>
          <TemplateSelector selectedTemplate={companyForm.default_template_delivery_challan || 'classic'} onSelect={(id) => setCompanyForm({ ...companyForm, default_template_delivery_challan: id })} />
        </div>
        <div className="space-y-2">
          <Label>Payment Receipt</Label>
          <TemplateSelector selectedTemplate={companyForm.default_template_payment_receipt || 'classic'} onSelect={(id) => setCompanyForm({ ...companyForm, default_template_payment_receipt: id })} />
        </div>
        <div className="space-y-2">
          <Label>Credit Note</Label>
          <TemplateSelector selectedTemplate={companyForm.default_template_credit_note || 'classic'} onSelect={(id) => setCompanyForm({ ...companyForm, default_template_credit_note: id })} />
        </div>
        <div className="space-y-2">
          <Label>Estimate / Proforma</Label>
          <TemplateSelector selectedTemplate={companyForm.default_template_estimate || 'classic'} onSelect={(id) => setCompanyForm({ ...companyForm, default_template_estimate: id })} />
        </div>
      </div>
      <div className="space-y-4 md:col-span-2 pt-4 border-t border-slate-200">
        <div className="space-y-2">
          <h3 className="font-heading text-base">Company Logo</h3>
          <p className="text-sm text-slate-500">The logo appears in the header of rendered documents. Save Company Settings to apply changes.</p>
        </div>
        {companyForm.logo_url ? (
          <img src={companyForm.logo_url} alt="Company logo" className="max-h-16 max-w-[180px] object-contain border border-slate-200 rounded-md p-2 bg-white" />
        ) : null}
        <div className="space-y-2">
          <Label htmlFor="company-logo">Logo image</Label>
          <Input id="company-logo" type="file" accept="image/*" onChange={handleLogoUpload} className="h-10" />
        </div>
        {companyForm.logo_url ? (
          <Button type="button" variant="outline" onClick={handleLogoRemove}>Remove Logo</Button>
        ) : null}
      </div>
              </div>
            )}
      <div className="flex items-center justify-between gap-4 rounded-md border border-slate-200 p-4">
        <div>
          <p className="font-medium text-slate-900">Automatic journal entries</p>
          <p className="text-sm text-slate-500">When on, finalizing an invoice and recording a payment automatically post balanced journal entries for this company. Default off.</p>
        </div>
        <Switch data-testid="auto-journal-switch" checked={!!companyForm.auto_journal_enabled} onCheckedChange={(v) => setCompanyForm((prev) => ({ ...prev, auto_journal_enabled: v }))} />
      </div>
          <div data-testid="integrations-section" className="rounded-md border border-slate-200 p-4 space-y-3">
            <div>
              <p className="font-medium text-slate-900">Maya integration</p>
              <p className="text-sm text-slate-500">Maya sends call-log and lead events to this company. Give Maya the receiver URL below and the API token saved here. Events from known clients appear on their activity timeline; unknown callers land in the Maya Leads inbox.</p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="maya-token">Maya API token</Label>
              <Input id="maya-token" data-testid="maya-token-input" type="password" value={mayaToken} onChange={(e) => setMayaToken(e.target.value)} placeholder={mayaTokenSaved ? "Saved - leave blank to keep it" : "Paste the Maya API token"} />
              {mayaTokenSaved && (<p className="text-sm text-slate-500">Saved token ending in {mayaTokenLast4}.</p>)}
            </div>
            <div className="flex flex-wrap gap-2">
              <Button type="button" data-testid="maya-save-btn" onClick={saveMayaToken} disabled={mayaSaving}>{mayaSaving ? "Saving..." : "Save token"}</Button>
              {mayaTokenSaved && (<Button type="button" variant="outline" data-testid="maya-remove-btn" onClick={removeMayaToken} disabled={mayaSaving}>Remove token</Button>)}
              <Button type="button" variant="outline" data-testid="maya-check-btn" onClick={checkMayaSetup}>Check setup</Button>
            </div>
            {mayaCheckMessage && (<p data-testid="maya-check-status" className="text-sm text-slate-600">{mayaCheckMessage}</p>)}
            <p className="text-sm text-slate-500 break-all">Receiver URL: {mayaReceiverUrl}</p>
          </div>
            <div className="border-t pt-4 mt-4">
              <p className="font-medium text-slate-900">Elite product catalogue seed</p>
              <p className="text-sm text-slate-500">Adds 26 Elite Flow Control products (flanges, valves, gaskets with sizes and pressure classes) to a new company. Prices are starter list prices to review. The seed refuses to run when the company already has products, so existing data is never overwritten.</p>
              <div className="flex items-center gap-2 mt-2">
                <Button onClick={handleSeedCatalogue} disabled={seedBusy || (seedStatus && seedStatus.already_seeded)} data-testid="seed-catalogue-btn" className="bg-teal-700 hover:bg-teal-800 rounded-[10px]">
                  {seedBusy ? "Seeding..." : "Seed Elite catalogue"}
                </Button>
                <Button variant="outline" onClick={loadSeedStatus} data-testid="seed-status-btn">Refresh status</Button>
              </div>
              {seedStatus && (
                <p className="text-sm text-slate-500 mt-2" data-testid="seed-status">
                  {seedStatus.already_seeded ? "This company already has " + seedStatus.product_count + " products, so the seed is disabled." : "This company has " + seedStatus.product_count + " products. The catalogue holds " + seedStatus.catalogue_size + " items."}
                </p>
              )}
              {seedMessage && (
                <p className="text-sm text-slate-500 mt-2" data-testid="seed-message">{seedMessage}</p>
              )}
            </div>
            <Button
              onClick={handleCompanySave}
              disabled={companySaving}
              data-testid="save-company-btn"
              className="bg-teal-700 hover:bg-teal-800 rounded-[10px]"
            >
              {companySaving ? "Saving..." : "Save Company Settings"}
            </Button>
          </CardContent>
        </Card>
      </TabsContent>
      <TabsContent value="import">
        <DataImport />
      </TabsContent>
      </Tabs>

      {/* Company Info */}
      <Card className="bg-[#F7F6F2] border-[#E5E2D9]">
        <CardContent className="p-6">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-[#0C1F1A] rounded-[10px] flex items-center justify-center">
              <img src="/merai-symbol.png" alt="Merai" className="w-8 h-8" />
            </div>
            <div>
              <p className="font-heading font-bold text-[#131A17]">Merai</p>
              <p className="text-sm text-[#5C6660]">Version 1.0.0</p>
         {process.env.REACT_APP_SUPPORT_EMAIL && (<p className="text-xs text-[#8A948E] mt-1">Support: {process.env.REACT_APP_SUPPORT_EMAIL}</p>)}
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default Settings;
